import { ref, get, serverTimestamp, remove } from "firebase/database";
import { collection, addDoc } from 'firebase/firestore';
import { database, db } from '../../firebase';
import { validateIssue } from '../../schemas/issueSchema';

export const refreshIssuesData = async () => {
  try {
    const kamEyePath = '/kam_eye';
    const kamEyeSnapshotRef = ref(database, kamEyePath);

    // Read data once
    const snapshot = await get(kamEyeSnapshotRef);

    const issuesData = snapshot.exists()
      ? Object.entries(snapshot.val()).map(([id, value]) => ({ ...value, id }))
      : [];

    return issuesData;
  } catch (error) {
    console.error('Error refreshing issues data:', error);
    return [];
  }
};

export const closeAllIssues = async (issuesToClose) => {
  const kam_eye_issues = collection(db, 'kam_eye');
  const kamEyePath = '/kam_eye';
  
  try {
    // Validate all issues
    const validIssues = issuesToClose.filter(issue => {
      if (!validateIssue(issue)) {
        console.warn('Invalid issue in closeAllIssues:', issue);
        return false;
      }
      return true;
    });

    if (validIssues.length === 0) {
      console.log('No valid issues to close');
      return { successful: 0, failed: 0 };
    }

    console.log(`Closing ${validIssues.length} issues in parallel...`);
    
    // Process all issues in parallel using Promise.allSettled
    const closePromises = validIssues.map(async (issue) => {
      try {
        let issueData = { ...issue };
        
        let closedTimestamp;
        let uniqueKey = 'Closed';
        let num = 1;

        // Osiris no longer writes a `timestamps` object at all (see createdAt) -
        // it may be entirely absent on a freshly-created issue.
        if (!issueData.timestamps) {
          issueData.timestamps = {};
        }
        if (!issueData.timestamps.close) {
          issueData.timestamps.close = {};
        }

        while (issueData.timestamps.close[uniqueKey]) {
          num++;
          uniqueKey = `Closed_${num}`;
        }
        closedTimestamp = serverTimestamp();

        issueData.timestamps.close[uniqueKey] = closedTimestamp;

        // Run Firestore add and Realtime Database remove in parallel
        const issuePath = `${kamEyePath}/${issue.id}`;
        const issueRef = ref(database, issuePath);

        await Promise.all([
          // Add to Firestore
          addDoc(kam_eye_issues, {
            ...issueData,
            originalIssueId: issue.id,
          }),
          // Remove from Realtime Database
          remove(issueRef)
        ]);

        return { success: true, issueId: issue.id };
      } catch (error) {
        console.error(`Error closing issue ${issue.id}:`, error);
        return { success: false, issueId: issue.id, error };
      }
    });

    // Wait for all operations to complete
    const results = await Promise.allSettled(closePromises);
    
    // Log results
    const successful = results.filter(result => result.status === 'fulfilled' && result.value.success).length;
    const failed = results.length - successful;
    
    console.log(`Completed: ${successful} successful, ${failed} failed`);
    
    return { successful, failed };
  } catch (error) {
    console.error('Error closing all issues:', error);
    throw error;
  }
};